import { matchingQuestionData } from '../../nodeCreationHelpers'
import { parseHtmlString } from 'ingestion/nodeCreationHelpers'
import {
  INoteData,
  ISimplifiedNode,
} from '../../../components/ingestion/IngestedTree'
import { IM360Parser } from './IM360Parser'

export function imGridStyling(content: string) {
  const asHtml = parseHtmlString(content)
  doImGrid(asHtml)
  return asHtml.body.innerHTML
}
export function removeManualSeparators(content: string) {
  return content.replaceAll(/<p>_+<\/p>/gm, '')
}

/**
 * Converts LaTeX expressions delimited by $ to \( \) notation.
 *
 * The function:
 * 1. Finds all potential LaTeX expressions using $...$
 * 2. Excludes cases where the $ is part of an HTML tag
 * 3. Converts valid LaTeX to \(...\) notation
 *
 * Special cases handled:
 * - Currency ($50) - skipped because starts with digit
 * - HTML tags with $ (<span>$</span>) - skipped by htmlTagMatch
 * - LaTeX with HTML (<br/>) - allowed and preserved
 */
export function latexConversion(content: string) {
  // Match $ followed by non-digit and any chars until next $
  const latexRegex = /\$(\D[^$]*)\$/g

  // Match any HTML tag opening or closing
  const htmlTagMatch = /<\/?[a-z]+>/

  // Find all potential LaTeX expressions
  const matches = content.matchAll(latexRegex)

  // Process each match, building up the result
  return Array.from(matches).reduce((acc, [currentContent, captured]) => {
    // Skip if the $ is part of an HTML tag
    if (htmlTagMatch.test(currentContent)) return acc

    // Replace $...$ with \(...\)
    return acc.replace(currentContent, `\\(${captured}\\)`)
  }, content)
}

// This finds elements with the imgrid property and adds flex styling to recreate the desired style
function doImGrid(html: Document) {
  const grids = html.getElementsByClassName('imgrid')
  Array.from(grids).forEach((grid) => {
    if (grid instanceof HTMLElement) {
      grid.style.padding = '20px 0 20px 0'
      doImGridImages(grid)
    }

    Array.from(grid.children).forEach((child) => {
      if (child.attributes.getNamedItem('class')?.value.includes('g--row')) {
        if (child instanceof HTMLElement) {
          doImGridRow(child)
        }
      }
    })
  })
}

function doImGridImages(grid: HTMLElement) {
  const imgs = grid.getElementsByTagName('img')
  Array.from(imgs).forEach((img) => {
    img.style.height = '200px'
  })
}

function doImGridRow(element: HTMLElement) {
  element.style.display = 'flex'
  element.style.setProperty('align-items', 'center')
  const cols = element.getElementsByClassName('g--column')
  Array.from(cols).forEach((column) => {
    if (column instanceof HTMLElement) {
      doImRowColumn(column)
    }
  })
}

const numberMap: Record<string, number> = {
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
  seven: 7,
  eight: 8,
  nine: 9,
  third: 3,
  fourth: 4,
  fifth: 5,
  sixth: 6,
  seventh: 7,
  eighth: 8,
  ninth: 9,
  tenth: 10,
  eleventh: 11,
  twelfth: 12,
}

const numbers = Object.keys(numberMap)

function getClassSizeValue(element: HTMLElement) {
  const classes = element.className
  if (classes.includes('g--full')) {
    return '1'
  }
  if (classes.includes('g--half')) {
    return '.5'
  }
  const classValue = classes.split(' ').find((name) => {
    const numberPart = name.split('g--').pop()?.split('-')[0]
    return numberPart && numbers.includes(numberPart)
  })
  if (!classValue) {
    // Not sure if this is the best way to handle this
    return 1
  }
  const [, , firstNumber, secondNumber, ...rest] = classValue.split('-')
  if (
    rest.length ||
    !firstNumber ||
    !secondNumber ||
    !(firstNumber in numberMap) ||
    !(secondNumber in numberMap)
  ) {
    throw new Error(`Bad Assumption: ${classValue}`)
  }
  return (numberMap[firstNumber] / numberMap[secondNumber]).toFixed(3)
}

function doImRowColumn(element: HTMLElement) {
  const itemWidthPart = getClassSizeValue(element)
  element.style.flex = `${itemWidthPart} 1 0`
}

export function createNoteNode(data: INoteData) {
  return IM360Parser.getInstance().createNoteNode(data)
}

export function createMatchingQuestion(data: matchingQuestionData) {
  return IM360Parser.getInstance().createMatching(data)
}

export function createDocumentNode<T = ISimplifiedNode>(
  title: string,
  nodes: T[],
) {
  return IM360Parser.getInstance().createDocumentNode<T>(title, nodes)
}
